import { isRecord } from '../common/is-record';
import type {
	PromptOfTheDayMetadata,
	PromptOfTheDayPrimarySource,
	PromptOfTheDayPrompt,
} from '../types';

const parseNonEmptyString = (value: unknown): string | null => {
	if (typeof value !== 'string') {
		return null;
	}

	const trimmed = value.trim();
	return trimmed === '' ? null : trimmed;
};

const parseNonEmptyStrings = (value: unknown): readonly string[] | undefined => {
	if (!Array.isArray(value)) {
		return undefined;
	}

	const strings = value.map(parseNonEmptyString).filter((item): item is string => item !== null);
	return strings.length > 0 ? strings : undefined;
};

// Optional and best-effort: anything malformed is dropped, never the prompt it belongs to.
const parseHighlights = (value: unknown): PromptOfTheDayMetadata['highlights'] => {
	if (!isRecord(value)) {
		return undefined;
	}

	const shortPrompt = parseNonEmptyStrings(value.shortPrompt);
	return shortPrompt !== undefined ? { shortPrompt } : undefined;
};

const parsePrimarySource = (value: unknown): PromptOfTheDayPrimarySource | undefined => {
	if (!isRecord(value)) {
		return undefined;
	}

	const url = parseNonEmptyString(value.url);
	const title = parseNonEmptyString(value.title);
	return url !== null ? { url, ...(title !== null && { title }) } : undefined;
};

const parseMetadata = (value: unknown): PromptOfTheDayMetadata | undefined => {
	if (!isRecord(value)) {
		return undefined;
	}

	const highlights = parseHighlights(value.highlights);
	const primarySource = parsePrimarySource(value.primarySource);
	if (highlights === undefined && primarySource === undefined) {
		return undefined;
	}

	return {
		...(highlights !== undefined && { highlights }),
		...(primarySource !== undefined && { primarySource }),
	};
};

const parsePrompt = (value: unknown): PromptOfTheDayPrompt | null => {
	if (!isRecord(value)) {
		return null;
	}

	const shortPrompt = parseNonEmptyString(value.shortPrompt);
	if (shortPrompt === null) {
		return null;
	}

	const title = parseNonEmptyString(value.title);
	const expandedPrompt = parseNonEmptyString(value.expandedPrompt);
	const sources = parseNonEmptyStrings(value.sources);
	const metadata = parseMetadata(value.metadata);

	return {
		shortPrompt,
		...(title !== null && { title }),
		...(expandedPrompt !== null && { expandedPrompt }),
		...(sources !== undefined && { sources }),
		...(metadata !== undefined && { metadata }),
	};
};

export const parsePromptsFromOutput = (output: unknown): readonly PromptOfTheDayPrompt[] | null => {
	if (!isRecord(output) || !Array.isArray(output.prompts)) {
		return null;
	}

	const prompts = output.prompts.map(parsePrompt);
	return prompts.length > 0 && prompts.every((prompt) => prompt !== null)
		? (prompts as readonly PromptOfTheDayPrompt[])
		: null;
};
